// enci.utils.js
import { debugLog } from './enci.log.js';

import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import htmlToPdfmake from 'html-to-pdfmake';

/**
 * Updates a .NET component using a provided helper and method.
 * @param {Object} dotNetHelper - The .NET helper object
 * @param {string} dotNetMethod - The name of the method to invoke
 * @param {*} data - The data to pass to the method
 * @returns {Promise<void>}
 */
export const updateDotNetComponent = async (dotNetHelper, dotNetMethod, data) => {
    if (!dotNetHelper) {
        console.warn(`DotNetHelper is not available for method: ${dotNetMethod}`);
        return;
    }

    try {
        await dotNetHelper.invokeMethodAsync(dotNetMethod, data);
        debugLog(`${dotNetMethod} called and updated Razor component`);
    } catch (error) {
        console.error(`Error in updateDotNetComponent: ${error}`);
    }
};

/**
 * Delays the execution for a given duration.
 * @param {number} duration - The duration to delay in milliseconds.
 * @returns {Promise<void>}
 */
export function delay(duration) {
    return new Promise(resolve => setTimeout(resolve, duration));
}

/**
 * Scrolls to the first element with the class 'invalid'.
 * The element is focused and scrolled into view smoothly.
 * @returns {Promise<void>}
 */
export async function scrollToFirstInvalidField() {
    const firstInvalidElement = document.querySelector('.invalid');
    if (firstInvalidElement) {
        firstInvalidElement.focus();
        firstInvalidElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
}

/**
 * Smoothly scrolls the window to the top.
 * @returns {Promise<void>}
 */
export async function scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
}

/**
 * Smoothly scrolls the specified container to the top.
 * @param {string} containerId - The ID of the container to scroll.
 * @returns {Promise<void>}
 */
export async function scrollToTopOfContainer(containerId) {
    const container = document.getElementById(containerId);
    if (container) {
        container.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
        console.error(`Container with ID '${containerId}' not found.`);
    }
}

/**
 * Waits for an element to appear in the DOM using MutationObserver and exponential backoff.
 * @param {string} selector - The CSS selector of the element to wait for.
 * @param {number} [maxAttempts=5] - Maximum number of attempts to find the element.
 * @param {number} [initialDelay=100] - Initial delay between attempts in milliseconds.
 * @returns {Promise<Element>} - Resolves with the found element.
 * @throws {Error} - Throws an error if the element is not found after maximum attempts.
 */
export function waitForElement(selector, maxAttempts = 10, initialDelay = 100) {
    return new Promise((resolve, reject) => {
        let attempts = 0;

        function tryFind() {
            const element = document.querySelector(selector);
            if (element) {
                resolve(element);
                return;
            }

            attempts++;
            if (attempts >= maxAttempts) {
                reject(
                    new Error(`Element with selector '${selector}' not found after ${maxAttempts} attempts.`)
                );
                return;
            }

            const delay = initialDelay * Math.pow(2, attempts - 1);
            setTimeout(tryFind, delay);
        }

        const observer = new MutationObserver((mutations, obs) => {
            const element = document.querySelector(selector);
            if (element) {
                resolve(element);
                obs.disconnect();
                return;
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        tryFind();
    });
}

/**
 * Checks if elements matching the given selector exist in the DOM.
 * @param {string} selector - The CSS selector to check.
 * @returns {boolean} - Returns true if one or more elements exist, otherwise false.
 */
export function exists(selector) {
    return document.querySelector(selector) !== null;
}

/**
 * Returns the inner html of an element with the passed id
 * after stripping out the comments
 * @param {string} elementId - The element ID to get the HTML from.
 * @returns {string} - Returns the inner html of the element
 */
export function getHtmlContent(elementId) {
    const element = document.getElementById(elementId);
    if (element) {
        let htmlContent = element.innerHTML;

        // Remove Blazor's injected comment-like placeholders <!--!-->
        htmlContent = htmlContent.replace(/<!--\s*!*\s*-->/g, "").trim();

        return htmlContent;
    }
    return "";
}

/**
 * Sets the inner html of the given element.
 * @param {object} element - The element to get the HTML from.
 * @param {string} html - The new HTML to set.
 * @returns {void}
 */
export function setInnerHTML(element, html)
{
    element.innerHTML = html;
};

/**
 * Returns the inner html of an element.
 * @param {string} element - The element to get the HTML from.
 * @returns {string} - Returns the inner html of the element
 */
export function getInnerHTML(element)
{
    return element.innerHTML;
};

export function downloadElementAsPdf(elementId, fileName) {
    // Locate the element by ID
    const element = document.getElementById(elementId);
    if (!element) {
        console.error("Element not found:", elementId);
        return;
    }


    var html = element.innerHTML;

    var options = {
        ignoreStyles: ['font', 'font-family', 'margin', 'padding'],
        defaultStyles: {
            img: { alignment: "center" },
            h1: { alignment: "center", fontSize: 16, bold: true, margin: [0, 0, 0, 0] },
            h2: { alignment: "center", fontSize: 14, bold: true, color: "#424242" },
            table: { alignment: "center", margin: [0, 0, 0, 0], width: "100%" },
            div: { alignment: "center" }
        },
    };

    var val = htmlToPdfmake(html, options);


    // Adjust the styles and table sizes...
    val.forEach((item) => {

        if (item.style && item.style.includes("footer"))
            item.marginTop = 20;
        if (item.style && item.style.includes("tableHeading"))
            item.marginTop = 20;
        if (item.nodeName === "HR" && item.canvas)
            item.canvas[0].lineColor = "#03508A";

        if (item.table) {
            item.table.widths = item.table.body[0].map(() => "*"); // Distribute columns equally

            var isSubHeading = item.style.includes('subHeading');

            if (isSubHeading) {
                item.layout = "noBorders";
            }
            else {
                // Apply background color to the first row (header row)
                item.table.body[0] = item.table.body[0].map((cell) => ({
                    text: cell.text || cell,
                    fillColor: "#c0d3e2",
                    bold: true,
                    //color: "white", // Makes text readable on red background
                }));
            }
        }
    });

    // Remove empty text elements that may add spacing
    val = val.filter((item) => {
        if (typeof item === "object" && typeof item.text === "string" && item.text.trim() === "") {
            return false;
        }
        return true;
    });

    // After the HTML is converted to the pdfMake syntax, you can debug it with this line...
    var debug = JSON.stringify(val, null, 2);
    console.log(debug);

    var dd = {
        content: val,
        pageBreakBefore: function (node) {
            return node.style && node.style.includes('page-break');
        },
        styles: {
            h1: { alignment: "center", fontSize: 18, bold: true },
            h2: { alignment: "center", fontSize: 16, bold: true },
            table: { alignment: "center", margin: [0, 0, 0, 0], width: "100%" },
            div: { alignment: "center" }
        },
    };

    pdfMake.addVirtualFileSystem(pdfFonts);
    pdfMake.createPdf(dd).download(fileName);
};

export async function svgToPdf(x, fileName) {
    x = "";

    const svgs = document.querySelectorAll("#divPdfContainerChart svg");
    const images = await Promise.all(Array.from(svgs).map(svgToImage));
    const watermark = await imageToBase64("/images/EnergyNet.png");

    const pdfContent = images.map(imgData => ({
        image: imgData,
        alignment: "center",
        width: 670 // Adjust as needed
    }));

    const docDefinition = {
        pageSize: "A4",
        pageOrientation: "landscape",
        content: pdfContent,
        background: function (currentPage, pageSize) {
            return {
                image: watermark,
                width: 120, // Adjust watermark size
                absolutePosition: { x: pageSize.width - 250, y: pageSize.height - 90 }, // Bottom right
                opacity: 1
                //opacity: 0.3
            };
        }
    };

    pdfMake.createPdf(docDefinition).download(fileName);
}

function svgToImage(svgElement) {
    return new Promise((resolve) => {
        const svgString = new XMLSerializer().serializeToString(svgElement);
        const canvas = document.createElement("canvas");
        canvas.width = svgElement.clientWidth;
        canvas.height = svgElement.clientHeight;
        const ctx = canvas.getContext("2d");

        const img = new Image();
        img.onload = function () {
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL("image/png"));
        };
        img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgString)));
    });
}


function imageToBase64(url) {
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.onload = function () {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL("image/png"));
        };
        img.src = url;
    });
}


export function saveSvgAsPng(svgElement, width, height, filename = 'image.png') {
    // 1. Serialize the SVG element to a string.
    const serializer = new XMLSerializer();
    let svgString = serializer.serializeToString(document.getElementById(svgElement));

    // Ensure the SVG has the proper namespace declarations.
    if (!svgString.match(/^<svg[^>]+xmlns="http:\/\/www\.w3\.org\/2000\/svg"/)) {
        svgString = svgString.replace(/^<svg/, '<svg xmlns="http://www.w3.org/2000/svg"');
    }
    if (!svgString.match(/^<svg[^>]+"http:\/\/www\.w3\.org\/1999\/xlink"/)) {
        svgString = svgString.replace(/^<svg/, '<svg xmlns:xlink="http://www.w3.org/1999/xlink"');
    }

    // Optional: add the XML declaration.
    svgString = '<?xml version="1.0" standalone="no"?>\r\n' + svgString;

    // 2. Create a Blob from the SVG string and a URL for the blob.
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    // 3. Create an image, set its source to the blob URL.
    const img = new Image();
    img.onload = function () {
        // 4. Create a canvas with the desired dimensions.
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d');

        // Draw the SVG image onto the canvas.
        context.drawImage(img, 0, 0, width, height);

        // Convert the canvas content to a PNG data URL.
        const pngDataUrl = canvas.toDataURL('image/png');

        // Create a temporary link to trigger the download.
        const downloadLink = document.createElement('a');
        downloadLink.href = pngDataUrl;
        downloadLink.download = filename;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);

        // Clean up the object URL.
        URL.revokeObjectURL(url);
    };

    // Start loading the image.
    img.src = url;
};

export function downloadFileFromStream(fileName, contentType, content) {
    const blob = new Blob([content], { type: contentType });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}